import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import useSEO from '../hooks/useSEO';
import RelatedSolutions from '../components/RelatedSolutions';
import CrossSellBlock from '../components/CrossSellBlock';

const { 
  FiCamera, FiVideo, FiSmartphone, FiZap, FiTarget, FiCheckCircle, 
  FiArrowRight, FiArrowDown, FiLayers, FiMessageSquare, FiActivity, 
  FiUsers, FiShield, FiPlus, FiPlusCircle, FiCpu, FiStar, FiGift 
} = FiIcons;

const ContentShootingOffer = () => {
  const { lang } = useLanguage();
  const TIDYCAL_URL = "https://tidycal.com/mathiasdeppe/free-15-minute-growth-review";

  const content = {
    en: {
      seo: {
        title: "Professional Content Shooting Days | Barcelona | Mathias Deppe",
        desc: "High-end content creation designed for conversion. We shoot the assets your AI Lead Assistant needs to turn attention into bookings."
      },
      hero: {
        h1: "Give Your Lead System Better Content to Work With",
        h2: "Your AI Lead Assistant can turn conversations into bookings. But the system still needs something to start the conversation.",
        body: "I come to your gym or clinic and create a bank of professional photos and short-form videos specifically designed to attract attention and start more conversations. One content day. Weeks of usable assets.",
        cta: "Add a Content Day →"
      },
      synergy: {
        h2: "Your AI handles the conversation. Your content creates the conversation.",
        steps: ["CONTENT", "ATTENTION", "DM", "AI ASSISTANT", "QUALIFICATION", "BOOKING"]
      },
      deliverables: {
        h2: "The Content Day",
        sub: "Short-form content designed to create attention and conversations.",
        items: [
          { title: "3-hour on-site shoot", desc: "Professional session at your facility." },
          { title: "20+ professional photos", desc: "High-end assets for posts and ads." },
          { title: "8–12 short-form videos", desc: "Vertical reels designed for engagement." },
          { title: "B-roll library", desc: "Raw background footage for future use." },
          { title: "Vertical-first content", desc: "Optimized for Instagram and TikTok." },
          { title: "Content ideas & hooks", desc: "Strategic scripts that grab attention." },
          { title: "Ready-to-post files", desc: "Edited and delivered for immediate use." }
        ]
      },
      niches: {
        clinic: {
          title: "Beauty Clinics",
          body: "Content that answers the questions your prospects already ask. E.g., 'What happens during...', 'Who is this treatment for?', 'Meet the practitioner'."
        },
        gym: {
          title: "Gyms",
          body: "Perfect conversation starters for your AI Agent. E.g., 'What does your first class look like?', 'Meet the coaches', 'Why people stay'."
        }
      },
      packages: {
        h2: "Investment & Packages",
        oneOff: {
          title: "One-Off Content Day",
          price: "€750",
          subtext: "Perfect to test the waters and build your initial asset library.",
          bullets: [
            "3-hour on-site shoot",
            "20+ professional photos",
            "8–10 short-form videos",
            "B-roll library",
            "Simple content ideas & hooks",
            "Ready-to-post vertical assets"
          ]
        },
        monthly: {
          title: "Monthly Content Partner",
          price: "€500 / month",
          subtext: "Every month, I create the content your acquisition system needs next.",
          tag: "MOST POPULAR",
          bullets: [
            "Monthly 3-hour on-site shoot",
            "20+ photos & 8–10 short-form videos",
            "B-roll library expansion",
            "Strategic content planning",
            "Ideas based on your current sales goals",
            "Prioritized assets for your AI Lead Assistant"
          ]
        }
      },
      bundle: {
        h2: "The Full Customer Acquisition System",
        sub: "AI Lead Assistant + Monthly Content Day",
        oldPrice: "€749",
        newPrice: "€550 / month + Performance",
        saveLabel: "SAVE €199",
        f1: "Monthly Content Day (Value: €500/mo) — Creates: Attention → Trust → Conversations",
        f2: "AI Lead Assistant (Value: €249/mo + Performance) — Handles: Comments → DMs → Qualify → Booking",
        benefit: "Save €199/month and get a complete, hands-off acquisition engine.",
        cta: "Claim the Bundle →"
      },
      cta: {
        h2: "Ready to fuel your lead system?",
        body: "Let's plan your shoot and build the assets your AI needs to thrive.",
        cta: "Book Your Shoot →"
      }
    },
    es: {
      seo: {
        title: "Días de Sesión de Contenido Profesional | Barcelona | Mathias Deppe",
        desc: "Creación de contenido de alta gama diseñado para la conversión. Grabamos el material que tu Asistente de Leads necesita."
      },
      hero: {
        h1: "Dale a tu Sistema de Leads Mejor Contenido con el que Trabajar",
        h2: "Tu Asistente de IA puede convertir conversaciones en reservas. Pero el sistema aún necesita algo para iniciar la conversación.",
        body: "Voy a tu gimnasio o clínica y creo un banco de fotos profesionales y vídeos cortos diseñados específicamente para captar la atención e iniciar más conversaciones. Un día de contenido. Semanas de material útil.",
        cta: "Añadir un Día de Contenido →"
      },
      synergy: {
        h2: "Tu IA maneja la conversación. Tu contenido crea la conversación.",
        steps: ["CONTENIDO", "ATENCIÓN", "DM", "ASISTENTE IA", "CUALIFICACIÓN", "RESERVA"]
      },
      deliverables: {
        h2: "El Día de Contenido",
        sub: "Contenido en formato corto diseñado para crear atención y conversaciones.",
        items: [
          { title: "Sesión presencial de 3 horas", desc: "Sesión profesional en tus instalaciones." },
          { title: "Más de 20 fotos profesionales", desc: "Activos de alta calidad para posts y anuncios." },
          { title: "8–12 vídeos cortos", desc: "Reels verticales diseñados para generar impacto." },
          { title: "Biblioteca de B-roll", desc: "Material de fondo para uso futuro." },
          { title: "Formato vertical nativo", desc: "Optimizado para Instagram y TikTok." },
          { title: "Ideas y ganchos", desc: "Guiones estratégicos que captan la atención." },
          { title: "Archivos listos para publicar", desc: "Editados y entregados para uso inmediato." }
        ]
      },
      niches: {
        clinic: {
          title: "Clínicas de Estética",
          body: "Contenido que responde a las preguntas que tus prospectos ya hacen. Ej., 'Qué ocurre durante...', '¿Para quién es este tratamiento?', 'Conoce al especialista'."
        },
        gym: {
          title: "Gimnasios",
          body: "Iniciadores de conversación perfectos para tu Agente de IA. Ej., '¿Cómo es tu primera clase?', 'Conoce al especialista', 'Por qué la gente se queda'."
        }
      },
      packages: {
        h2: "Inversión y Paquetes",
        oneOff: {
          title: "Día de Contenido Único",
          price: "750 €",
          subtext: "Perfecto para probar y construir tu biblioteca inicial de material.",
          bullets: [
            "Sesión presencial de 3 horas",
            "Más de 20 fotos profesionales",
            "8–10 vídeos cortos",
            "Biblioteca de B-roll",
            "Ideas y ganchos de contenido simples",
            "Archivos verticales listos para publicar"
          ]
        },
        monthly: {
          title: "Partner de Contenido Mensual",
          price: "500 € / mes",
          subtext: "Cada mes, creo el contenido que tu sistema de adquisición necesita a continuación.",
          tag: "MÁS POPULAR",
          bullets: [
            "Sesión presencial mensual de 3 horas",
            "Más de 20 fotos y 8–10 vídeos cortos",
            "Expansión de la biblioteca de B-roll",
            "Planificación estratégica de contenido",
            "Ideas basadas en tus objetivos de ventas actuales",
            "Material priorizado para tu Asistente de IA"
          ]
        }
      },
      bundle: {
        h2: "El Sistema Completo de Adquisición de Clientes",
        sub: "Asistente de IA para Leads + Día de Contenido Mensual",
        oldPrice: "749 €",
        newPrice: "550 € / mes + Rendimiento",
        saveLabel: "AHORRA 199 €",
        f1: "Día de Contenido Mensual (Valor: 500 €/mes) — Crea: Atención → Confianza → Conversaciones",
        f2: "Asistente de IA (Valor: 249 €/mes + Rendimiento) — Maneja: Comentarios → DMs → Cualificar → Reserva",
        benefit: "Ahorra 199 €/mes y obtén un motor de adquisición completo y automatizado.",
        cta: "Reclamar el Paquete →"
      },
      cta: {
        h2: "¿Listo para alimentar tu sistema de leads?",
        body: "Planifiquemos tu sesión y creemos el material que tu IA necesita para triunfar.",
        cta: "Reserva tu Sesión →"
      }
    }
  };

  const t = content[lang] || content.en;
  useSEO(t.seo.title, t.seo.desc);

  const relatedLinks = [
    { 
      path: "/ai-lead-agent", 
      label: lang === 'es' ? "Asistente de Leads con IA" : "AI Lead Assistant",
      description: lang === 'es' ? "El sistema que convierte el contenido en reservas." : "The system that turns content into bookings."
    },
    { 
      path: "/portfolio", 
      label: lang === 'es' ? "Ver Portafolio" : "View Portfolio",
      description: lang === 'es' ? "Ejemplos visuales de nuestro trabajo." : "Visual examples of our content work."
    }
  ];

  return (
    <div className="bg-zinc-950 text-white min-h-screen selection:bg-orange-500/30">
      {/* 1. HERO SECTION */}
      <section className="relative pt-40 pb-24 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-orange-500/5 blur-[120px] pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-4xl md:text-7xl font-black tracking-tighter leading-[0.9] mb-8 uppercase">
              {t.hero.h1}
            </h1>
            <h2 className="text-xl md:text-3xl font-bold text-orange-500 mb-8 max-w-4xl mx-auto tracking-tight">
              {t.hero.h2}
            </h2>
            <p className="max-w-3xl mx-auto text-zinc-400 text-lg md:text-xl leading-relaxed mb-12">
              {t.hero.body}
            </p>
            <a href={TIDYCAL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-10 py-5 bg-white text-zinc-950 font-black rounded-full hover:bg-orange-500 hover:text-white transition-all transform hover:scale-105 shadow-2xl">
              {t.hero.cta}
            </a>
          </motion.div>
        </div>
      </section>

      {/* 2. SYNERGY FLOW */}
      <section className="py-24 px-6 bg-zinc-900/30 border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-black text-center mb-16 tracking-tighter uppercase text-zinc-400">
            {t.synergy.h2}
          </h2>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-2">
            {t.synergy.steps.map((step, i) => (
              <React.Fragment key={i}>
                <motion.div 
                  whileInView={{ opacity: 1, scale: 1 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`px-6 py-4 rounded-xl font-black text-xs uppercase tracking-widest text-center min-w-[140px] ${i === 0 ? 'bg-white text-zinc-950' : i === 3 ? 'bg-orange-500 text-white' : 'bg-zinc-900 text-zinc-500 border border-white/5'}`}
                >
                  {step}
                </motion.div>
                {i < t.synergy.steps.length - 1 && (
                  <div className="flex items-center justify-center py-2">
                    <SafeIcon icon={FiArrowRight} className="text-zinc-700 text-xl hidden md:block" />
                    <SafeIcon icon={FiArrowDown} className="text-zinc-700 text-xl md:hidden" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* 3. DELIVERABLES */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black mb-4 tracking-tighter uppercase">{t.deliverables.h2}</h2>
            <p className="text-xl text-zinc-500 font-medium">{t.deliverables.sub}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.deliverables.items.map((item, i) => (
              <motion.div 
                key={i}
                whileInView={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="p-8 bg-zinc-900 border border-white/5 rounded-3xl hover:border-orange-500/30 transition-all group"
              >
                <div className="w-12 h-12 bg-zinc-950 rounded-xl flex items-center justify-center mb-6 border border-white/5 group-hover:bg-orange-500/10 transition-colors">
                  <SafeIcon icon={[FiCamera, FiLayers, FiVideo, FiSmartphone, FiZap, FiTarget, FiCheckCircle][i] || FiCheckCircle} className="text-2xl text-orange-500" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 uppercase tracking-tight">{item.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. NICHE EXAMPLES */}
      <section className="py-24 px-6 bg-zinc-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-10 bg-zinc-950 border border-white/5 rounded-[3rem] group hover:border-orange-500/20 transition-all">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-orange-500/10 rounded-2xl flex items-center justify-center">
                  <SafeIcon icon={FiActivity} className="text-2xl text-orange-500" />
                </div>
                <h3 className="text-2xl font-black uppercase tracking-tighter">{t.niches.clinic.title}</h3>
              </div>
              <p className="text-zinc-400 text-lg leading-relaxed italic">
                "{t.niches.clinic.body}"
              </p>
            </div>
            <div className="p-10 bg-zinc-950 border border-white/5 rounded-[3rem] group hover:border-orange-500/20 transition-all">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-orange-500/10 rounded-2xl flex items-center justify-center">
                  <SafeIcon icon={FiUsers} className="text-2xl text-orange-500" />
                </div>
                <h3 className="text-2xl font-black uppercase tracking-tighter">{t.niches.gym.title}</h3>
              </div>
              <p className="text-zinc-400 text-lg leading-relaxed italic">
                "{t.niches.gym.body}"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. PRICING PACKAGES */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-black text-center mb-16 tracking-tighter uppercase">
            {t.packages.h2}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto mb-24">
            {/* One-Off Card */}
            <motion.div 
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              viewport={{ once: true }}
              className="p-10 bg-zinc-900 border border-white/10 rounded-[3rem] flex flex-col relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 p-8 opacity-5">
                <SafeIcon icon={FiCamera} className="text-6xl" />
              </div>
              <h3 className="text-2xl font-black uppercase tracking-tighter text-white mb-2">{t.packages.oneOff.title}</h3>
              <p className="text-zinc-500 mb-6 text-sm font-medium leading-relaxed">{t.packages.oneOff.subtext}</p>
              <div className="text-5xl font-black text-white mb-8 tracking-tighter">{t.packages.oneOff.price}</div>
              <ul className="space-y-4 mb-10 flex-1">
                {t.packages.oneOff.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-zinc-300">
                    <SafeIcon icon={FiCheckCircle} className="text-zinc-600 mt-1 shrink-0" />
                    <span className="text-sm font-bold uppercase tracking-tight">{bullet}</span>
                  </li>
                ))}
              </ul>
              <a href={TIDYCAL_URL} target="_blank" rel="noopener noreferrer" className="w-full py-4 bg-zinc-800 text-white font-black rounded-full hover:bg-zinc-700 transition-colors text-center uppercase tracking-widest text-xs">
                {t.cta.cta}
              </a>
            </motion.div>

            {/* Monthly Partner Card */}
            <motion.div 
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-10 bg-zinc-900 border border-white/10 rounded-[3rem] flex flex-col relative overflow-hidden"
            >
              <div className="absolute top-6 right-6 px-3 py-1 bg-zinc-800 text-zinc-400 text-[10px] font-black rounded-full tracking-widest uppercase border border-white/5">
                {t.packages.monthly.tag}
              </div>
              <h3 className="text-2xl font-black uppercase tracking-tighter text-white mb-2">{t.packages.monthly.title}</h3>
              <p className="text-zinc-500 mb-6 text-sm font-medium leading-relaxed">{t.packages.monthly.subtext}</p>
              <div className="text-5xl font-black text-white mb-8 tracking-tighter">{t.packages.monthly.price}</div>
              <ul className="space-y-4 mb-10 flex-1">
                {t.packages.monthly.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-white">
                    <SafeIcon icon={FiCheckCircle} className="text-orange-500 mt-1 shrink-0" />
                    <span className="text-sm font-bold uppercase tracking-tight">{bullet}</span>
                  </li>
                ))}
              </ul>
              <a href={TIDYCAL_URL} target="_blank" rel="noopener noreferrer" className="w-full py-4 bg-zinc-800 text-white font-black rounded-full hover:bg-orange-500 hover:text-white transition-all text-center uppercase tracking-widest text-xs">
                {t.cta.cta}
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 6. PREMIUM BUNDLE PRICING CARD */}
      <section className="py-24 px-6 bg-zinc-900/20">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            whileInView={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 40 }}
            viewport={{ once: true }}
            className="relative group"
          >
            {/* Animated Glow Backdrop */}
            <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-orange-600 rounded-[4rem] blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200" />
            
            <div className="relative p-12 md:p-16 bg-zinc-900 border-2 border-orange-500 rounded-[4rem] overflow-hidden">
              <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
                <SafeIcon icon={FiGift} className="text-9xl text-orange-500" />
              </div>

              <div className="text-center mb-12">
                <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-orange-500 text-white text-[10px] font-black tracking-[0.2em] uppercase rounded-full mb-8">
                  <SafeIcon icon={FiStar} className="text-xs" /> ULTIMATE BUNDLE
                </span>
                <h2 className="text-3xl md:text-6xl font-black text-white mb-4 tracking-tighter uppercase leading-[0.9]">
                  {t.bundle.h2}
                </h2>
                <p className="text-xl md:text-2xl font-bold text-orange-500 uppercase tracking-tight">
                  {t.bundle.sub}
                </p>
              </div>

              <div className="flex flex-col items-center mb-16">
                <div className="flex items-center gap-4 mb-2">
                  <span className="text-2xl md:text-3xl font-black text-zinc-600 line-through tracking-tighter">
                    {t.bundle.oldPrice}
                  </span>
                  <div className="px-3 py-1 bg-green-500/10 border border-green-500/20 rounded-full text-green-500 text-[10px] font-black tracking-widest uppercase">
                    {t.bundle.saveLabel}
                  </div>
                </div>
                <div className="text-6xl md:text-8xl font-black text-white tracking-tighter text-center">
                  {t.bundle.newPrice}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
                <div className="p-8 bg-zinc-950/50 border border-white/5 rounded-[2.5rem] relative group/item">
                  <div className="w-12 h-12 bg-white text-zinc-950 rounded-2xl flex items-center justify-center mb-6">
                    <SafeIcon icon={FiCamera} className="text-xl" />
                  </div>
                  <p className="text-white font-bold text-lg leading-snug uppercase tracking-tight">
                    {t.bundle.f1}
                  </p>
                </div>
                <div className="p-8 bg-zinc-950/50 border border-orange-500/10 rounded-[2.5rem] relative group/item">
                  <div className="w-12 h-12 bg-orange-500 text-white rounded-2xl flex items-center justify-center mb-6">
                    <SafeIcon icon={FiCpu} className="text-xl" />
                  </div>
                  <p className="text-white font-bold text-lg leading-snug uppercase tracking-tight">
                    {t.bundle.f2}
                  </p>
                </div>
              </div>

              <div className="text-center">
                <p className="text-zinc-400 font-medium mb-12 max-w-xl mx-auto italic">
                  "{t.bundle.benefit}"
                </p>
                <a href={TIDYCAL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-4 px-12 py-6 bg-white text-zinc-950 text-xl md:text-2xl font-black rounded-full hover:bg-orange-500 hover:text-white transition-all transform hover:scale-105 shadow-[0_0_50px_rgba(255,255,255,0.1)] group/btn">
                  {t.bundle.cta}
                  <SafeIcon icon={FiArrowRight} className="group-hover/btn:translate-x-2 transition-transform" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cross-Sell Loop */}
      <CrossSellBlock variant="content" />

      {/* Internal Linking */}
      <RelatedSolutions links={relatedLinks} />

      {/* 7. FINAL CTA */}
      <section className="py-32 px-6 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div 
            whileInView={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 20 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-7xl font-black text-white mb-8 tracking-tighter uppercase">
              {t.cta.h2}
            </h2>
            <p className="text-xl md:text-2xl text-zinc-400 mb-12 max-w-2xl mx-auto leading-relaxed">
              {t.cta.body}
            </p>
            <a href={TIDYCAL_URL} target="_blank" rel="noopener noreferrer" className="px-12 py-6 text-2xl font-black text-zinc-950 bg-white rounded-full hover:bg-orange-500 hover:text-white transition-all duration-300 transform hover:scale-105 shadow-2xl">
              {t.cta.cta}
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContentShootingOffer;